import type { ErrorDescription } from './rpcErrors';

/**
 * A failed Storage call -> the copy a dispatcher reads (design 7, last paragraph). storage-js errors carry
 * `statusCode` (the Storage API's own, a string) and/or `status` (the HTTP status). The raw message is
 * never shown.
 */

/** The portal's own marker: the upload answered, but not with the object it was sent (a non-JSON 200,
 *  the SPA's index.html from a host that does not proxy /storage, or a `data.path` that differs). */
export const UNEXPECTED_ANSWER = { message: 'storage: unexpected answer', statusCode: 'unexpected' } as const;

function field(err: unknown, key: string): unknown {
  return typeof err === 'object' && err !== null ? (err as Record<string, unknown>)[key] : undefined;
}

export function describeStorageError(err: unknown): ErrorDescription {
  const message = String(field(err, 'message') ?? '');
  const code = String(field(err, 'statusCode') ?? field(err, 'status') ?? '');

  if (code === UNEXPECTED_ANSWER.statusCode) return { key: 'errors.storageUnreachable' };
  if (/Failed to fetch|NetworkError|Load failed|fetch failed/i.test(message)) return { key: 'errors.network' };
  if (code === '413' || /payload too large|maximum allowed size/i.test(message)) return { key: 'errors.fileTooLarge' };
  if (code === '415' || /mime type/i.test(message)) return { key: 'errors.fileType' };
  if (/already exists/i.test(message)) return { key: 'errors.retry' };
  if (code === '403' || /row-level security|unauthorized/i.test(message)) return { key: 'errors.notAllowedRun' };
  if (code === '404' || /not found/i.test(message)) return { key: 'errors.notFound', keyValues: { thing: 'entities.document' } };
  return { key: 'errors.storageUnreachable' };
}
